import io from 'socket.io-client';

import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EventService } from './event.service';
import { CookieService } from 'ngx-cookie-service';

type SocketIOCallback = (data: any) => void;

@Injectable({ providedIn: 'root' })
export class SocketIOService {

	private socketServerURL = environment.urls.socketServerURL;

	private socket: any | null = null;
	private isConnected = false;
	private pendingListeners: Array<{ event: string; callback: SocketIOCallback }> = [];

	// Notification count streams
	private _unreadMessages = new BehaviorSubject<number>(0);
	private _unreadInterviews = new BehaviorSubject<number>(0);
	private _unreadOffers = new BehaviorSubject<number>(0);
	private _unreadOnboard = new BehaviorSubject<number>(0);
	unreadMessages$ = this._unreadMessages.asObservable();
	unreadInterviews$ = this._unreadInterviews.asObservable();
	unreadOffers$ = this._unreadOffers.asObservable();
	unreadOnboard$ = this._unreadOnboard.asObservable();

	// Connection status stream
	private _connectionStatus = new BehaviorSubject<'connected' | 'disconnected' | 'connecting'>('disconnected');
	connectionStatus$ = this._connectionStatus.asObservable();

	// SyncViewUpdate stream
	private _syncViewSubject = new Subject<any>();
	syncViewUpdate$ = this._syncViewSubject.asObservable();

	constructor(private eventService: EventService,
		private cookieService: CookieService) {
	}

	// Returns the callback so callers can hand it back to removeListener() —
	// removing by reference (not by name) is required because multiple
	// independent components listen on the same event name on this shared
	// socket; removing "by name" would silently unsubscribe every other
	// listener too.
	on(eventName: string, callback: SocketIOCallback): SocketIOCallback {
		if (this.socket !== null) {
			this.socket.on(eventName, callback);
		} else {
			// Socket not created yet — queue and apply when connect() runs
			this.pendingListeners.push({ event: eventName, callback });
		}
		return callback;
	}

	emit(eventName: string, data: any) {
		if (this.socket !== null) {
			this.socket.emit(eventName, data);
		}
	}

connect() {
    if (this.isConnected || !this.cookieService.check('token')) return;

    this._connectionStatus.next('connecting');

    this.socket = io(this.socketServerURL, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      withCredentials: true
    } as any);

    this.isConnected = true;

    // Apply any listeners that were registered before the socket was created
    this.pendingListeners.forEach(({ event, callback }) => {
      this.socket!.on(event, callback);
    });
    this.pendingListeners = [];

    this.socket.on('connect', () => {
      this._connectionStatus.next('connected');
      const tokenData = {
        AuthToken: this.cookieService.get('token'),
        UserType: '1',
      };
      this.socket!.emit('AuthorizationToken', tokenData);
    });

    this.eventService.subscribe('mark-read', (payload: any) => {
      this.socket!.emit('MarkAsReadMessage', payload.markedMessage);
    });

    this.socket.on('SuccessMarkAsReadMsg', (data: any) => {
      this.eventService.broadcast('SuccessMarkAsReadMsg', data);
    });

    this.socket.on('InterviewAccepted', (data: any) => {
      this.eventService.broadcast('InterviewAccepted', data);
    });

    this.socket.on('Exception', (data: any) => {
      console.error('[Socket Exception]', data);
    });

    this.socket.on('disconnect', (reason: string) => {
      console.warn('[Socket] Disconnected:', reason);
      this.isConnected = false;
      this._connectionStatus.next('disconnected');
      if (reason === 'io server disconnect') {
        // Server disconnected us (e.g. auth failure) - do not auto-reconnect
      } else {
        // Transport error, timeout etc - socket.io will auto-reconnect
      }
    });

    this.socket.on('MessageReceived', (data: any) => {
      this._unreadMessages.next(this._unreadMessages.value + 1);
    });

    this.socket.on('InterviewReceived', (data: any) => {
      this._unreadInterviews.next(this._unreadInterviews.value + 1);
    });

    this.socket.on('NewJobOffer', (data: any) => {
      this._unreadOffers.next(this._unreadOffers.value + 1);
    });

    this.socket.on('OfferWithdrew', (data: any) => {
      const current = this._unreadOffers.value;
      if (current > 0) this._unreadOffers.next(current - 1);
    });

    this.socket.on('ReceiveOnboard', (data: any) => {
      this._unreadOnboard.next(this._unreadOnboard.value + 1);
    });

    this.socket.on('SyncViewUpdate', (data: any) => {
      this._syncViewSubject.next(data);
    });
  }


	reconnect(): void {
		if (this.socket && !this.socket.connected) {
			this.socket.connect();
		} else if (!this.socket) {
			this.connect();
		}
	}

	disconnect() {
		if (this.socket !== null) {
			this.socket.disconnect();
			this.socket = null;
			this.isConnected = false;
		}
	}

	sendApplication(applicationData: any) {
		if (this.socket !== null) {
			if (this.socket.connected) {
				this.socket.emit('JobApplied', applicationData);
			}
		}
	}

	sendMessage(messageData: any) {
		if (this.socket !== null) {
			if (this.socket.connected) {
				this.socket.emit('SendMessage', messageData);
			} else {
				this.socket.once('connect', () => {
					this.socket!.emit('SendMessage', messageData);
				});
				this.socket.connect();
			}
		}
	}

	interviewMarkAsRead(interviewData: any, recruitId: any) {
		if (this.socket !== null) {
			let tokenData: any = {};
			tokenData.token = this.cookieService.get('token');
			tokenData.userType = '1';
			tokenData.userId = recruitId;
			tokenData.interview_content_id = interviewData;
			this.socket.emit('InterviewMarkAsRead', tokenData);
			this.eventService.broadcast('interviewMarkAsRead', interviewData);
		}
	}

	acceptInterview(acceptedInterviewData: any) {
		if (this.socket !== null) {
			this.socket.emit('AcceptInterview', acceptedInterviewData);
		}
	}

	declineInterview(interviewData: any) {
		if (this.socket !== null) {
			this.socket.emit('DeclineInterview', interviewData);
		}
	}

	requestRescheduleInterview(interviewData: any) {
		if (this.socket !== null) {
			this.socket.emit('RequestRescheduleInterview', interviewData);
		}
	}

	syncViewStatus(syncViewData: any) {
		if (this.socket !== null) {
			this.socket.emit('SyncView', syncViewData);
		}
	}

	// Notifies the recruiter who created the assignment that a timesheet
	// was just submitted for their approval - relay routes this to
	// UT2_<recruiterUserId> and Hire's dashboard-header shows a toast/badge.
	submitTimesheet(payload: { recruiterUserId: number; talentName: string; assignmentId: number; timesheetId: number; periodMonth: number; periodYear: number; customerName?: string; contractNumber?: string }) {
		if (this.socket !== null) {
			this.socket.emit('SubmitTimesheet', payload);
		}
	}

	resetUnreadMessages() { this._unreadMessages.next(0); }
	resetUnreadInterviews() { this._unreadInterviews.next(0); }
	resetUnreadOffers() { this._unreadOffers.next(0); }
	resetUnreadOnboard() { this._unreadOnboard.next(0); }

	getConnectionStatus() {
		return this.socket ? this.socket.connected : this.socket;
	}

	// Pass the exact callback returned by on() to remove only that listener.
	// Falling back to removeAllListeners(name) when no callback is given is
	// kept only for backward compatibility - it wipes every listener for
	// that event on the shared socket, including ones owned by other
	// components, so all current callers pass their callback explicitly.
	removeListener(name: any, callback?: SocketIOCallback) {
		if (this.socket !== null) {
			if (callback) {
				this.socket.off(name, callback);
			} else {
				this.socket.removeAllListeners(name);
			}
		}
	}

	offerMarkAsRead(offerData: any, recruitId: any) {
		if (this.socket !== null) {
			let tokenData: any = {};
			tokenData.token = this.cookieService.get('token');
			tokenData.userType = '1';
			tokenData.userId = recruitId;
			tokenData.offer_content_id = offerData;
			this.socket.emit('OfferMarkAsRead', tokenData);
			this.eventService.broadcast('offerMarkAsRead', offerData);
		}
	}

	AcceptOffer(hireUserId: any, offerContentId: any) {
		if (this.socket !== null) {
			let tokenData: any = {};
			tokenData.hire_user_id = hireUserId
			tokenData.offer_content_id = offerContentId
			this.socket.emit('AcceptOffer', tokenData);
		}
	}

	DeclineOffer(hireUserId: any, offerContentId: any) {
		if (this.socket !== null) {
			let tokenData: any = {};
			tokenData.hire_user_id = hireUserId
			tokenData.offer_content_id = offerContentId


			this.socket.emit('DeclineOffer', tokenData);
		}
	}

	OfferResponseAttachment(hireUserId: any, offerContentId: any) {
		if (this.socket !== null) {
			let tokenData: any = {};
			tokenData.hire_user_id = hireUserId
			tokenData.offer_content_id = offerContentId
			this.socket.emit('OfferResponseAttachment', tokenData);
		}
	}

}
